import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Globe, FloppyDisk, ArrowsCounterClockwise, ArrowSquareOut } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Keys defined in /app/backend/node/config.js — keep in sync.
const SCRAPER_KEYS = [
  { key: "SCRAPER_URL_ULTRAGYM", label: "UltraGym Source URL",
    hint: "The page Arya scrapes to extract UltraGym variant prices (Starter, Ultimate, Performance).",
    fallback: "https://portl.co/product/ultragym/" },
  { key: "SCRAPER_URL_STUDIO",   label: "Studio Source URL",
    hint: "The page Arya scrapes to extract the Portl Studio price + stock state.",
    fallback: "https://portl.co/product/studio/" }
];

export default function ScraperUrlsTab() {
  const { fetchWithAuth } = useAuth();
  const [values, setValues]   = useState({});  // current saved values
  const [drafts, setDrafts]   = useState({});  // editable values
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    const res = await fetchWithAuth(`${API_URL}/api/settings`);
    if (!res.ok) { toast.error(res.data?.error || "Failed to load settings"); return; }
    const map = {};
    for (const s of (res.data.settings || [])) {
      if (s.key && SCRAPER_KEYS.some(k => k.key === s.key)) {
        // The /api/settings list endpoint returns the safe-to-show value as
        // `display_value` (sensitive fields are masked here too — but the
        // scraper URLs are non-sensitive, so this is the actual current value).
        map[s.key] = s.display_value || "";
      }
    }
    setValues(map);
    setDrafts(map);
    setLoading(false);
  }, [fetchWithAuth]);

  useEffect(() => { load(); }, [load]);

  const dirty = SCRAPER_KEYS.filter(k => (drafts[k.key] || "") !== (values[k.key] || ""));

  const save = async () => {
    if (dirty.length === 0) return;
    setSaving(true);
    try {
      const updates = {};
      for (const k of dirty) updates[k.key] = drafts[k.key] || "";
      const res = await fetchWithAuth(`${API_URL}/api/settings`, { method: "PUT", data: { updates } });
      if (!res.ok) { toast.error(res.data?.error || "Save failed"); return; }
      toast.success(`Saved ${dirty.length} URL${dirty.length !== 1 ? "s" : ""}. The next scraper run will use them.`);
      await load();
    } finally { setSaving(false); }
  };

  const refreshNow = async () => {
    setRefreshing(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/prices/refresh`, { method: "POST" });
      if (!res.ok) { toast.error(res.data?.error || "Refresh failed"); return; }
      const updated = res.data?.summary?.updated?.length || 0;
      const failed  = res.data?.summary?.failed?.length || 0;
      toast.success(`Scraper run complete. ${updated} updated, ${failed} failed.`);
    } finally { setRefreshing(false); }
  };

  if (loading) return <div className="text-[#666] text-sm py-12 text-center" data-testid="scraper-urls-loading">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="scraper-urls-tab">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
            <Globe size={20} weight="duotone" className="text-[#E85D24]" />
            Scraper URLs
          </h2>
          <p className="text-sm text-[#888] mt-1.5 max-w-2xl">
            The price scraper reads these pages every 24 hours (and on demand) to keep <strong className="text-white">portl_studio</strong>, <strong className="text-white">ultragym_starter</strong>, <strong className="text-white">ultragym_ultimate</strong>, and <strong className="text-white">ultragym_performance</strong> rows in <code className="text-[10px] text-[#888]">product_prices</code> fresh. Override here if portl.co restructures its URL paths in future. Leave blank to fall back to the built-in defaults.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={refreshNow}
            disabled={refreshing}
            data-testid="scraper-refresh-btn"
            className="px-3 py-1.5 rounded text-xs font-medium border border-[#2A2A2A] text-[#888] hover:border-[#444] hover:text-white transition-colors disabled:opacity-40">
            <ArrowsCounterClockwise size={12} weight="bold" className={`inline mr-1 -mt-0.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Scraping…" : "Run scraper now"}
          </button>
          <button
            onClick={save}
            disabled={saving || dirty.length === 0}
            data-testid="scraper-save-btn"
            className={`px-4 py-1.5 rounded text-xs font-bold transition-all ${
              dirty.length === 0
                ? "bg-[#1A1A1A] text-[#444] border border-[#222] cursor-not-allowed"
                : "bg-[#E85D24] text-white hover:bg-[#FF6A2C]"
            }`}>
            <FloppyDisk size={12} weight="bold" className="inline mr-1 -mt-0.5" />
            {dirty.length === 0 ? "No changes" : `Save ${dirty.length} change${dirty.length !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {SCRAPER_KEYS.map(k => {
          const value = drafts[k.key] ?? "";
          const isDirty = (drafts[k.key] || "") !== (values[k.key] || "");
          const isOverridden = !!(values[k.key] || "").trim();
          return (
            <div
              key={k.key}
              data-testid={`scraper-url-row-${k.key}`}
              className={`border rounded-lg p-5 transition-colors ${isDirty ? "border-[#E85D24]/40 bg-[#1A0F0A]/30" : "border-[#1A1A1A]"}`}>
              <div className="flex items-baseline justify-between gap-3 mb-2">
                <label htmlFor={`url-${k.key}`} className="text-sm font-semibold text-white">{k.label}</label>
                {isOverridden ? (
                  <span className="text-[10px] uppercase tracking-wider text-[#FFD60A] font-bold">Custom override</span>
                ) : (
                  <span className="text-[10px] uppercase tracking-wider text-[#666]">Default in use</span>
                )}
              </div>
              <p className="text-[12px] text-[#888] mb-3">{k.hint}</p>
              <div className="flex items-center gap-2">
                <input
                  id={`url-${k.key}`}
                  data-testid={`scraper-url-input-${k.key}`}
                  type="url"
                  value={value}
                  placeholder={k.fallback}
                  onChange={(e) => setDrafts(d => ({ ...d, [k.key]: e.target.value }))}
                  className="flex-1 bg-[#0A0A0A] border border-[#222] rounded px-3 py-2 text-sm text-white placeholder:text-[#444] focus:outline-none focus:border-[#E85D24]/50 font-mono"
                />
                {(value || k.fallback) && (
                  <a
                    href={value || k.fallback}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open in new tab"
                    data-testid={`scraper-url-open-${k.key}`}
                    className="px-3 py-2 rounded border border-[#222] text-[#888] hover:text-white hover:border-[#444] transition-colors shrink-0">
                    <ArrowSquareOut size={14} weight="bold" />
                  </a>
                )}
              </div>
              {!isOverridden && (
                <p className="text-[11px] text-[#555] mt-2 font-mono">Fallback: {k.fallback}</p>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-[#555] leading-relaxed">
        Saved changes take effect on the next scraper run (daily, or "Run scraper now" above). The current cached prices remain valid until the next successful scrape.
      </p>
    </div>
  );
}
